import { useEffect, useState } from "react";
import { fetchCategories, fetchGoods, fetchPartners, fetchSets } from "../api";
import { ALL_CATEGORY_KEY } from "../i18n";
import type { AddableItem, CartEntry, Dessert, DessertSet, Partner } from "../types";

export function useBakeryCatalogState() {
  const [desserts, setDesserts] = useState<Dessert[]>([]);
  const [sets, setSets] = useState<DessertSet[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [apiCategories, setApiCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORY_KEY);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDessert, setSelectedDessert] = useState<Dessert | null>(null);
  const [detailQty, setDetailQty] = useState(1);
  const [selectedSet, setSelectedSet] = useState<DessertSet | null>(null);
  const [setQty, setSetQty] = useState(1);
  const [cart, setCart] = useState<CartEntry[]>([]);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    // Goods drive the loading state (the home grid waits on them). Sets and
    // categories load alongside but fail independently so one bad endpoint
    // doesn't blank the others.
    fetchGoods()
      .then((data) => {
        if (isMounted) {
          setDesserts(data);
        }
      })
      .catch((error) => {
        // Backend unreachable or misconfigured — show an empty catalog rather
        // than stale seed data.
        console.error("Failed to load goods from the store API:", error);
        if (isMounted) {
          setDesserts([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    fetchSets()
      .then((data) => {
        if (isMounted) {
          setSets(data);
        }
      })
      .catch((error) => {
        console.error("Failed to load sets from the store API:", error);
        if (isMounted) {
          setSets([]);
        }
      });

    fetchCategories()
      .then((data) => {
        if (isMounted) {
          setApiCategories(data);
        }
      })
      .catch((error) => {
        console.error("Failed to load categories from the store API:", error);
        if (isMounted) {
          setApiCategories([]);
        }
      });

    fetchPartners()
      .then((data) => {
        if (isMounted) {
          setPartners(data);
        }
      })
      .catch((error) => {
        console.error("Failed to load partners from the store API:", error);
        if (isMounted) {
          setPartners([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Prefer the categories endpoint; fall back to ones derived from the loaded
  // goods if it's empty or unreachable.
  const categoryNames =
    apiCategories.length > 0
      ? apiCategories
      : Array.from(new Set(desserts.map((dessert) => dessert.category)));
  const categories = [ALL_CATEGORY_KEY, ...categoryNames];

  const filteredDesserts = desserts.filter((dessert) => {
    const matchCategory =
      selectedCategory === ALL_CATEGORY_KEY || dessert.category === selectedCategory;
    const normalizedQuery = searchQuery.toLowerCase();
    const matchSearch =
      dessert.name.toLowerCase().includes(normalizedQuery) ||
      dessert.description.toLowerCase().includes(normalizedQuery);
    return matchCategory && matchSearch;
  });

  const addToCart = (item: AddableItem, qty = 1) => {
    setCart((previousItems) => {
      const existingItem = previousItems.find((cartItem) => cartItem.id === item.id);
      if (existingItem) {
        return previousItems.map((cartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + qty }
            : cartItem
        );
      }
      return [...previousItems, { ...item, quantity: qty }];
    });
  };

  const updateQty = (id: number, delta: number) => {
    setCart((previousItems) =>
      previousItems
        .map((item) => (item.id === id ? { ...item, quantity: item.quantity + delta } : item))
        .filter((item) => item.quantity > 0)
    );
  };

  const removeItem = (id: number) =>
    setCart((previousItems) => previousItems.filter((item) => item.id !== id));

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const openDetail = (dessert: Dessert) => {
    setSelectedDessert(dessert);
    setDetailQty(1);
  };

  const openSet = (set: DessertSet) => {
    setSelectedSet(set);
    setSetQty(1);
  };

  const addSetToCart = (set: DessertSet, qty = 1) => {
    addToCart(
      {
        id: set.id,
        name: set.name,
        price: set.price,
        image: set.mainImage,
        isSet: true
      },
      qty
    );
  };

  return {
    loading,
    desserts,
    sets,
    partners,
    categories,
    filteredDesserts,
    selectedCategory,
    setSelectedCategory,
    searchQuery,
    setSearchQuery,
    selectedDessert,
    setSelectedDessert,
    detailQty,
    setDetailQty,
    selectedSet,
    setSelectedSet,
    setQty,
    setSetQty,
    cart,
    cartOpen,
    setCartOpen,
    cartCount,
    cartTotal,
    openDetail,
    openSet,
    addToCart,
    addSetToCart,
    updateQty,
    removeItem
  };
}
